import React, { useMemo, useRef, useState } from "react";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import { RCCAlert, RCCButton, RCCDropDownItem, RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCServicesApi, RCCTooltip, RCCSelectOptionsCard } from "react-common-components";
import { useForm } from "react-hook-form";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import FiltersContainer from "src/components/FiltersContainer";
import Loader from "src/components/Loader";
import LovSectors from "src/components/Lov/Sectors";
import LovUsers from "src/components/Lov/Users";
import PageTitle from "src/components/PageTitle";
import Select from "src/components/Select";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import extractSingleValueFromArrayOfObject from "src/utils/extractSingleValueFromArrayOfObject";
import formatDate from "src/utils/formatDate";
import FiltersTagsContainer from "src/components/FiltersTagsContainer";
import { IRefFiltersTagsContainer, TagsTypes } from "src/components/FiltersTagsContainer/interface";
import { useAuth } from "src/context/Auth/context";
import { getCookie, setCookie } from "src/utils/cookies";

import ButtonExpandGroupers from "src/components/ButtonExpandGroupers";
import LovClassifications from "src/components/Lov/Classifications";
import LovPresentationGroups from "src/components/Lov/PresentationGroups";
import { getLocalStorage, setLocalStorage } from "src/utils/localStorage";
import { IRefIndicatorModal } from "src/components/ModalIndicator/interface";
import IndicatorModal from "src/components/ModalIndicator";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import DropdownWithBackground from "src/components/DropdownWithBackground";
import getUrlSearchParams from "src/utils/getUrlSearchParams";
import putUrlSearchParams from "src/utils/putUrlSearchParams";
import LovMaps from "src/components/Lov/Maps";
import LovPerspectives from "src/components/Lov/Perspectives";
import LovObjectives from "src/components/Lov/Objectives";
import LovGrouperIndicators from "src/components/Lov/GrouperIndicators";
import AppIcons from "src/components/AppIcons";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import Reports from "src/components/Reports";
import ReportGroupers from "src/components/Reports/interface";
import LovBudgetPlans from "src/components/Lov/BudgetPlans";
import DateTimePicker from "src/components/DateTimePicker";
import isValidDate from "src/utils/isValidDate";
import stringToDate from "src/utils/stringToDate";
import ScreenStandardGroup from "src/Interfaces/screenStandardGroup";
import IFiltersIndicatorsPanel, { IGroupersIndicators, IGroupersIndicatorsSelect, IndicatorsGroupers, IndicatorShowValues } from "./interface";
import GrouperOfIndicators from "./components/grouperOfIndicators";
import { IRefActionsModalShareIndicators } from "./components/ModalShareIndicator/interface";
import ModalIndicatorShare from "./components/ModalShareIndicator";
import { IIndicatorsLevel } from "./components/interface";
import { IndicatorsPanelContext } from "./indicatorsPanelContext";

const IndicatorsPanel: React.FC = () => {
	const params = useParams<{
        indicatorId: string;
    }>();

	const query = useLocation();
	const objQueryParams = getUrlSearchParams(query.search);

	const { translate } = useTranslation();
	const { confirm } = useConfirmModal();
	const { addToast } = RCCHookToast();
	const { user } = useAuth();
	
	// Modal Indicator
	const refModal = useRef<IRefIndicatorModal>(null);
	const refModalShare = useRef<IRefActionsModalShareIndicators>(null);

	const [layoutList, setLayoutList] = useState<"card" | "list">(getLocalStorage(`IndicatorScreenLayoutList-${user.efa005Id}`) === "card" ? "card" : "list");

	const history = useHistory();
	const [showCheckboxes, setShowCheckboxes] = useState(false);
	const [selectedCheckboxesId, setSelectedCheckboxesId] = useState<Array<number>>([]);
	const addSelectedCheckboxId = (id: number) => {
		setSelectedCheckboxesId((prevState) => {
			const updateState = [...prevState];
			const index = updateState.indexOf(id);
			if(index === -1)
				return [...updateState, id];
			updateState.splice(index, 1);
			return updateState;
		});
	};
	const addAllSelectedCheckboxesId = (id: number) => {
		setSelectedCheckboxesId((prevState) => {
			const updateState = [...prevState];
			const index = updateState.indexOf(id);
			if(index === -1)
				return [...updateState, id];
			return updateState;
		});
	};

	const filtersTagsRef = useRef<IRefFiltersTagsContainer>(null);
	const [showTagsFilters, setShowTagsFilters] = useState(false);
	const [showFilters, setShowFilters] = useState(false);
	const [myIndicators, setMyIndicators] = useState(false);
	const [filters, setFilters] = useState<IFiltersIndicatorsPanel>({ 
		f: objQueryParams?.f || "", 
		responsible: objQueryParams?.responsible || "",
		presentationGroup: objQueryParams?.presentationGroup || "",
		sector: objQueryParams?.sector || "",
		startDate: objQueryParams?.startDate ? stringToDate(objQueryParams.startDate) : null,
		endDate: objQueryParams?.endDate ? stringToDate(objQueryParams.endDate) : null,
		classification: objQueryParams?.classification || "",
		tags: objQueryParams?.tags || "",
		level: objQueryParams?.level || "",
		situation: objQueryParams?.situation || "",
		map: objQueryParams?.map || "",
		perspective: objQueryParams?.perspective || "",
		objective: objQueryParams?.objective || "",
		indicatorsId: params.indicatorId || "",
		budgetPlan: objQueryParams?.budgetPlan || "",
		currentPeriod: objQueryParams?.currentPeriod || ""
	});
	const { 
		handleSubmit : handleSubmitFilters, 
		register : registerFilters, 
		control: controlFilters, 
		reset: resetFilters, 
		setValue: setValueFilters 
	} = useForm();

	const submitFilters = async (data: any) => {
		const dataForm = data;
		if(data.situation)
			dataForm.situation = extractSingleValueFromArrayOfObject("value", data.situation)?.toString();
		
		if(typeof data.presentationGroup === "object")
			dataForm.presentationGroup = data.presentationGroup.value;
		if(typeof data.responsible === "object")
			dataForm.responsible = data.responsible.value;
		if(typeof data.sector === "object")
			dataForm.sector = data.sector.value;
		if(typeof data.classification === "object")
			dataForm.classification = data.classification.value;
		if(typeof data.level === "object")
			dataForm.level = data.level.value;
		if(typeof data.map === "object")
			dataForm.map = data.map.value;
		if(typeof data.perspective === "object")
			dataForm.perspective = data.perspective.value;
		if(typeof data.objective === "object")
			dataForm.objective = data.objective.value;
		if(typeof data.budgetPlan === "object")
			dataForm.budgetPlan = data.budgetPlan.value;

		if(myIndicators)
			dataForm.responsible = user.efa005Id.toString();

		dataForm.indicatorsId = "";
		dataForm.situation = "";

		putUrlSearchParams(dataForm);
		setFilters(dataForm);
	};
	const submitFiltersTags = async () => {
		setFilters((prevState) => {
			const updateState = {...prevState};
			updateState.tags = filtersTagsRef.current?.response || "";
			return updateState;
		});
	};

	const buttonSubmitFormGroupersRef = useRef<HTMLButtonElement>(null);
	const { handleSubmit : handleSubmitGroupers, control: controlGroupers } = useForm();
	const [defaultGrouper, setDefaultGrouper] = useState<IndicatorsGroupers | undefined>(getCookie(ScreenStandardGroup.INDICATORS) as IndicatorsGroupers);
	const [groupersSelect, setGroupersSelect] = useState<IGroupersIndicatorsSelect>({
		group: objQueryParams?.group as IndicatorsGroupers || defaultGrouper || IndicatorsGroupers.MAP,
		baseYear: objQueryParams?.baseYear || new Date().getFullYear().toString()
	});
	const submitGroupers = async (data: any) => {
		const dataFormatted = {
			...data,
			baseYear: !isValidDate(data.baseYear) ? new Date().getFullYear().toString() : data.baseYear.getFullYear().toString()
		};
		putUrlSearchParams({
			...dataFormatted,
			...filters
		});
		setGroupersSelect({
			...dataFormatted
		});
	};
	const [showValues, setShowValues] = useState<IndicatorShowValues>(IndicatorShowValues.ACCORDING_TO_INDICATOR);
	const [selectedMap, setSelectedMap] = useState(objQueryParams?.map || "");
	const [selectedPerspective, setselectedPerspective] = useState(objQueryParams?.perspective || "");
	const [groupers, setGroupers] = useState<IGroupersIndicators[]>([]);

	const { errorFetching, loading, loadData } = useEffectFetchData<IGroupersIndicators[]> (
		"Indicators/ListGroupers", 
		{ 
			...groupersSelect,
			f: filters.f || "",
			responsible: filters.responsible || "",
			presentationGroup: filters.presentationGroup,
			sector: filters.sector || "",
			startDate: filters.startDate ? formatDate(filters.startDate) : "",
			endDate: filters.endDate ? formatDate(filters.endDate) : "",
			classification: filters.classification || "",
			tags: filters.tags || "",
			level: filters.level || "",
			situation: filters.situation || "",
			indicatorsId: filters.indicatorsId || "",
			map: filters.map || "",
			perspective: filters.perspective || "",
			objective: filters.objective || "",
			budgetPlan: filters.budgetPlan || "",
			currentPeriod: filters.currentPeriod || ""
		 },
		 ({ data }) => {
			setGroupers(data);
		 },
		 [groupersSelect, filters],
		 true
	);

	const setAsDefaultGrouper = async () => {
		const {group} = groupersSelect;
		try{
			await RCCServicesApi.postRequest({
				method: "Indicators/SetDefaultGrouper",
				body: {
					group
				}
			});
			setDefaultGrouper(group);
			setCookie(ScreenStandardGroup.INDICATORS, group);
			addToast({
				color: "green",
				description: translate("Agrupador padrão atualizado com sucesso"),
				title: translate("Sucesso")
			});
		}catch(err){
			standardErrorMessages(err, translate("Erro ao alterar agrupador padrão"), addToast);
		}

	};

	const [allCollapsedExceptIndex, setAllCollapsedExceptIndex] = useState<number | undefined>(undefined);
	const executeCollapseAllExceptIndex = (index?: number) => {
		setAllCollapsedExceptIndex(index);
	};

	const Groupers = useMemo(() => loading ? (
		<Loader />
	) : (
		<>
			{errorFetching ? (
				<RCCAlert 
					title={translate("Atenção")}
					description={translate("Erro ao tentar encontrar resultados para esta pesquisa")}
				/>
			): (
				<>
					{groupers.length === 0 ? (
						<RCCAlert 
							title={translate("Atenção")}
							description={translate("Nenhum agrupador encontrado")}
						/>
					): (
						<IndicatorsPanelContext.Provider
							value={{
								showCheckboxes,
								addSelectedCheckboxId,
								actionHideCheckboxes: () => setShowCheckboxes(false),
								addAllSelectedCheckboxesId,
								selectedCheckboxesId
							}}
						>
							{groupers.map((grouper, index: number) => (
								<GrouperOfIndicators 
									key={`${grouper.id}-${grouper.id2}-${grouper.id3}-grouper-indicators`}
									filters={filters}
									grouper={grouper}
									groupersSelect={groupersSelect}
									layoutList={layoutList}
									showValues={showValues}
									expandedProp={allCollapsedExceptIndex === undefined ? undefined : allCollapsedExceptIndex === index}
									onCallbackChangeValuesIndicator={(reset: boolean) => executeCollapseAllExceptIndex(reset ? undefined : index)}
								/>
							))}
						</IndicatorsPanelContext.Provider>
					)}
				</>
			)}
		</>
	), [loading, errorFetching, groupers, showValues, layoutList, allCollapsedExceptIndex, showCheckboxes]);


	return (
		<>

			{/* Header -> Results + Buttons */}
			<PageTitle
				title={translate("Painel de Indicadores")}
				icon={() => <AppIcons name="indicators" />}
			>
				<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
					
					<RCCTooltip
						text={translate("Novo indicador")}
						color="purple"
					>
						<RCCButton
							icon={{name: "plus", color: "#FFF", size: 18}}
							color="purple"
							layout="isRounded"
							onClick={() => { refModal?.current?.actionButtonAdd(() => loadData());}}
							size="md"
						/>
					</RCCTooltip>
				</CheckPermission>
				<RCCTooltip
					text={translate("Selecionar indicadores")}
					color={showCheckboxes ? "blue" : "grey"}
				>
					<RCCButton
						icon={{name: "checkSquare", color: "#FFF", size: 16}}
						color={showCheckboxes ? "blue" : "grey"}
						layout="isRounded"
						onClick={() => setShowCheckboxes((prevState) => !prevState)}
						size="md"
					/>
				</RCCTooltip>
				{showCheckboxes && (
					<DropdownWithBackground
						position="bottomEnd"
						callerComponent={<RCCButton 
							icon={{
								name: "dotsVertical", color: "#FFF", size: 18
							}}
							color="grey"
							layout="isRounded"
							size="md"
						/>}
					>
						<RCCDropDownItem 
							selfEnclose
							onClick={() => {
								if(selectedCheckboxesId.length === 0){
									addToast({
										color: "blue",
										description: translate("Selecione ao menos um indicador"),
										title: translate("Atenção")
									});
									return;
								}
								const indicatorsId = selectedCheckboxesId.join(",");
								history.push(`/indicators/presentation/selected/${indicatorsId}`);
							}}
						>
							<RCCIcon name="eye" />
							{translate("Apresentar")}
						</RCCDropDownItem>
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
							<RCCDropDownItem 
								selfEnclose
								onClick={() => {
									if(selectedCheckboxesId.length === 0){
										addToast({
											color: "blue",
											description: translate("Selecione ao menos um indicador"),
											title: translate("Atenção")
										});
										return;
									}
									const indicatorsId = selectedCheckboxesId.join(",");
									refModalShare.current?.actionButtonOpenShareIndicators(indicatorsId);
								}}
							>
								<RCCIcon name="users" />
								{translate("Compartilhar")}
							</RCCDropDownItem>
						</CheckPermission>
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
							<RCCDropDownItem 
								selfEnclose
								onClick={() => {
									if(selectedCheckboxesId.length === 0){
										addToast({
											color: "blue",
											description: translate("Selecione ao menos um indicador"),
											title: translate("Atenção")
										});
										return;
									}
									confirm(
										translate("Marcar para atualizar"), 
										`${translate("Deseja realmente marcar para atualizar os valores dos indicadores")}?`,
										async () => {
											await RCCServicesApi.postRequest({
												method: "Indicators/MarkToUpdate",
												body: {
													indicators: selectedCheckboxesId,
													baseYear: groupersSelect.baseYear,
													periodId: null
												}
											});
							
											addToast({
												color: "green",
												description: `${translate("Valores dos indicadores marcados para atualizar com sucesso")}. ${translate("Será feito na próxima execução da integração")}`,
												title: translate("Sucesso")
											});
										},
										(err) => {
											standardErrorMessages(err, translate("Erro ao atualizar valores dos indicadores"), addToast);
										},
										false
									);
								}}
							>
								<RCCIcon name="settings" />
								{translate("Marcar para atualizar")}
							</RCCDropDownItem>
						</CheckPermission>
					</DropdownWithBackground>
				)}
				<RCCTooltip
					text={layoutList === "card" ? translate("Alterar para lista") : translate("Alterar para cards")}
					color="grey"
				>
					<RCCButton
						icon={{
							name: layoutList === "card" ? "list" : "table", 
							color: "#FFF", 
							size: 16
						}}
						color="grey"
						layout="isRounded"
						size="md"
						onClick={() => {
							if(layoutList === "card"){
								setLayoutList("list");
								setLocalStorage(`IndicatorScreenLayoutList-${user.efa005Id}`, "list");
								return;
							}
							setLocalStorage(`IndicatorScreenLayoutList-${user.efa005Id}`, "card");
							setLayoutList("card");
						}}
					/>
				</RCCTooltip>		

				<ButtonExpandGroupers />
				<Reports grouper={ReportGroupers.INDICATORS}/>
				<RCCTooltip
					text={`${translate("Filtros")} Tags`}
					color={showTagsFilters || filters.tags ? "blue" : "grey"}
				>
					<RCCButton
						icon={{name: "tags", color: "#FFF", size: 16}}
						color={showTagsFilters || filters.tags ? "blue" : "grey"}
						layout="isRounded"
						size="md"
						onClick={() => setShowTagsFilters((prevState) => !prevState)}
					/>
				</RCCTooltip>
				<RCCTooltip
					text={translate("Filtros")}
					color={showFilters || 
							filters.endDate || 
							filters.f || 
							filters.responsible || 
							filters.sector || 
							filters.presentationGroup || 
							filters.startDate ||
							filters.map ||
							filters.perspective ||
							filters.objective
						 ? "blue" : "grey"}
				>
					<RCCButton
						icon={{name: "filter", color: "#FFF", size: 16}}
						color={showFilters ||
								filters.endDate || 
								filters.f || 
								filters.responsible || 
								filters.sector || 
								filters.presentationGroup || 
								filters.startDate ||
								filters.map ||
								filters.perspective ||
								filters.objective
							 ? "blue" : "grey"}
						layout="isRounded"
						size="md"
						onClick={() => setShowFilters((prevState) => !prevState)}
					/>
				</RCCTooltip>
			</PageTitle>

			{/* Filters */}
			<FiltersContainer
				showFilters={showFilters}
				onSubmitFilters={handleSubmitFilters(submitFilters)}
				resetFilters={() => resetFilters({ 
					f: "", 
					responsible: { value: "", label: ""},
					presentationGroup: { value: "", label: ""},
					sector: { value: "", label: ""},
					startDate: null,
					endDate: null,
					classification: { value: "", label: "" },
					indicatorsId: "",
					level: { value: "", label: "" },
					map: { value: "", label: "" },
					perspective: { value: "", label: "" },
					objective: { value: "", label: "" },
					budgetPlan: { value: "", label: "" } 
				})}
			>
				<RCCGrid
					container
					item
					spacing={1}
				>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<DateTimePickerRange
							control={controlFilters}
							label={translate("Período")}
							labelSeparator={translate("até")}
							nameInit = "startDate"
							nameEnd = "endDate"
							defaultValueEnd={filters.endDate}
							defaultValueInit={filters.startDate}
							type="datePicker"
							setValue={setValueFilters}
							props={{
								datePicker: {
									format: "dd/MM",
									views: ["month", "date"]
								}
							}}
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovPresentationGroups 
							control={controlFilters}
							name="presentationGroup"
							label={translate("Grupo de apresentação")}
							defaultValue={filters.presentationGroup}
							isClearable
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovUsers 
							control={controlFilters}
							name="responsible"
							label={translate("Responsável")}
							defaultValue={filters.responsible}
							disabled={myIndicators}
							isClearable
						/>
					</RCCGrid>
				</RCCGrid>

				<RCCGrid
					container
					item
					spacing={1}
				>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovSectors 
							control={controlFilters}
							name="sector"
							label={translate("Setor")}
							defaultValue={filters.sector}
							isClearable
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovClassifications
							control={controlFilters}
							name="classification"
							label={translate("Classificação")}
							isClearable
						/> 
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<Select
							name="level" 
							label={translate("Nível")}
							control={controlFilters}
							defaultValue={filters.level}
							isClearable
							options={[{
								label: translate("Estratégico"),
								value: IIndicatorsLevel.STRATEGIC
							},{
								label: translate("Tático"),
								value: IIndicatorsLevel.TACTIC
							},{
								label: translate("Operacional"),
								value: IIndicatorsLevel.OPERATIONAL
							}]}
						/>
					</RCCGrid>
				</RCCGrid>

				<RCCGrid
					container
					item
					spacing={1}
				>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovMaps 
							control={controlFilters}
							name="map"
							label={translate("Mapas")}
							defaultValue={filters.map}
							isClearable
							onChangeCapture={(e:any) => {
								setSelectedMap(e?.value||"");
								setselectedPerspective("");
								setValueFilters("perspective","");
							}}
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovPerspectives
							key={selectedMap} 
							control={controlFilters}
							name="perspective"
							label={translate("Perspectivas")}
							defaultValue={filters.perspective}
							extraParametersOnLoad={{
								map:selectedMap
							}}
							onChangeCapture={(e:any) => {
								setselectedPerspective(e?.value||"");
								setValueFilters("objective","");
							}}
							cacheOptions={false}
							isClearable
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovObjectives
							key={selectedPerspective}  
							control={controlFilters}
							name="objective"
							label={translate("Objetivo")}
							defaultValue={filters.objective}
							extraParametersOnLoad={{
								map:selectedMap,
								perspective:selectedPerspective
							}}
							cacheOptions={false}
							isClearable
						/>
					</RCCGrid>
				</RCCGrid>

				<RCCGrid
					container
					item
					spacing={1}
				>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovBudgetPlans 
							control={controlFilters}
							name="budgetPlan"
							label={translate("Plano orçamentário")}
							defaultValue={filters.budgetPlan}
							isClearable
						/>
					</RCCGrid>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<RCCInput
							name="f" 
							label={translate("Pesquisar")}
							register={registerFilters}
							defaultValue={filters.f}
						/>
					</RCCGrid>
				</RCCGrid>
          
			</FiltersContainer>
			
			{/* Filters Tags */}
			<FiltersTagsContainer 
				ref={filtersTagsRef}
				onSubmitFilters={submitFiltersTags}
				tagType={TagsTypes.INDICATORS}
				showFilters={showTagsFilters}
			/>

			{/* Groupers Select -> Origin -> Delayed */}
			<form onSubmit={handleSubmitGroupers(submitGroupers)} style={{marginBottom: "1rem"}}>
				<RCCGrid
					container
					item
					spacing={1}
				>
					<RCCGrid
						container
						item
						xs={12}
						sm={6}
						md={4}
						lg={3}
						alignItems="baseline"
					>
						<LovGrouperIndicators 
							control={controlGroupers}
							name="group"
							defaultValue={objQueryParams?.group as IndicatorsGroupers || defaultGrouper || IndicatorsGroupers.MAP}
							onChangeCapture={() => buttonSubmitFormGroupersRef.current?.click()}
							styleInline="width: auto; flex: 1;"
						/>
						<RCCTooltip
							color="grey"
							text={translate("Definir como agrupador padrão")}
						>
							<RCCButton 
								color="transparent"
								icon={{
									name: "checkBookmark",
									color: "var(--grey)"
								}}
								onClick={setAsDefaultGrouper}
							/>
						</RCCTooltip>
					</RCCGrid>
						
					<RCCGrid
						container
						item
						xs={12}
						sm={4}
						md={3}
						lg={2}
						alignItems="center"
					>
						<DateTimePicker
							type="datePicker" 
							control={controlGroupers}
							name="baseYear"
							label={translate("Ano base")}
							defaultValue={new Date(Number(groupersSelect.baseYear), 0, 1)}
							onChange={(data) => {
								if(isValidDate(data))
									buttonSubmitFormGroupersRef.current?.click();
							}}
							props={{
								datePicker: {
									format: "yyyy",
									views: ["year"]
								}
							}}
						/>		
					</RCCGrid>

					<RCCGrid
						container
						item
						xs={12}
						sm={6}
						md={4}
						lg={3}
					>
						<Select 
							control={controlGroupers}
							name="showValues"
							label={translate("Mostrar valor")}
							options={[
								{ value: IndicatorShowValues.ACCORDING_TO_INDICATOR, label: `${translate("Conforme indicador")}` },
								{ value: IndicatorShowValues.PERCENT, label: `${translate("Percentual")}` },
								{ value: IndicatorShowValues.ABSOLUTE_VALUE, label: `${translate("Valor absoluto")}` }
							]}
							defaultValue={showValues}
							onChangeCapture={(e) => setShowValues(e.value as IndicatorShowValues)}
						/>
					</RCCGrid>

					<RCCGrid
						container
						item
						xs={12}
						sm={12}
						lg={4}
						justifyContent="flex-end"
					>
						<RCCSelectOptionsCard
							defaultValue={myIndicators ? "S" : "N"}
							options={[{
								value: "N",
								label: translate("Todos indicadores")
							},{
								value: "S",
								label: translate("Meus indicadores")
							}]}
							label="&nbsp;"
							onChange={(e) => {
								setValueFilters("responsible", { value: "", label: ""});
								if(e === "S"){
									setMyIndicators(true);
									setFilters({
										...filters,
										responsible: user.efa005Id.toString()
									});
									return;
								}
								setMyIndicators(false);
								setFilters({
									...filters,
									responsible: ""
								});
							}}
						/>
					</RCCGrid>

				</RCCGrid>
				<button 
					aria-label="Submit Form"
					ref={buttonSubmitFormGroupersRef}
					type="submit" 
					style={{ display: "none" }}
				></button>
			</form>				
			
			{/* Groupers */}
			{Groupers}

			<IndicatorModal ref={refModal}/>
			<ModalIndicatorShare ref={refModalShare} />
		</>
	);
};

export default IndicatorsPanel;
